import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {  faComments, faBook, faVideo, faCalculator, faDownload, faCheckCircle, faEnvelope, faPhone, faGlobe } from "@fortawesome/free-solid-svg-icons";
import { Breadcrumb } from "../components/Breadcrumb";
const ResourcesPage = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6
      }
    }
  };

  return (
    <div className="resources-page">

      <Breadcrumb currentName={"Resources"} currentParentName={"Resources"} />
      {/* Hero Section */}
      <section className="container-fluid " style={{ backgroundColor: 'var(--light-bg)' }}>
        <div className="container">
          <motion.div
            className="text-center py-5"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <motion.h1
              className="fw-bold display-4 mb-4"
              style={{ color: 'var(--heading)' }}
              variants={fadeInUp}
            >
              Resources
            </motion.h1>
            <motion.p
              className="lead fs-3 mb-4"
              style={{ color: 'var(--text)' }}
              variants={fadeInUp}
            >
              Your Financial Knowledge Hub — Learn, Plan & Grow with Confidence
            </motion.p>
            <motion.p
              className="fs-5 mx-auto"
              style={{ color: 'var(--text)', maxWidth: '800px' }}
              variants={fadeInUp}
            >
              At <strong style={{ color: 'var(--primary)' }}>SAG Financials</strong>, we believe that an informed client is an empowered client. Financial planning isn't just about trusting an advisor — it's about understanding your money, your goals, and the choices available to you.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Why Education Matters */}
      <section className="container py-5">
        <motion.div
          className="row align-items-center"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <div className="col-lg-6">
            <motion.h2
              className="fw-bold mb-4"
              style={{ color: 'var(--heading)' }}
              variants={itemVariants}
            >
              Why Financial Education Matters
            </motion.h2>
            <motion.p
              className="mb-4 fs-5"
              style={{ color: 'var(--text)' }}
              variants={itemVariants}
            >
              Most people are taught how to earn money — not how to manage it.
            </motion.p>
            <motion.p
              style={{ color: 'var(--text)' }}
              variants={itemVariants}
            >
              Whether it's understanding mutual funds, decoding insurance jargon, planning for taxes, or simply learning how SIPs work — financial literacy gives you the clarity and confidence to take control of your future.
            </motion.p>
          </div>
          <div className="col-lg-6">
            <motion.div
              className="p-4 rounded-3"
              style={{ backgroundColor: 'var(--primary)', color: 'var(--white)' }}
              variants={itemVariants}
            >
              <h4 className="fw-bold mb-3">Our Mission</h4>
              <p className="mb-0">
                Simplify complex financial topics and break them down into easy, actionable knowledge — no technical jargon, no pushy sales talk, just clear information you can trust.
              </p>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* What You'll Find */}
      <section className="container-fluid py-5" style={{ backgroundColor: 'var(--light-bg)' }}>
        <div className="container">
          <motion.div
            className="text-center mb-5"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <h2 className="fw-bold mb-4" style={{ color: 'var(--heading)' }}>What You'll Find Here</h2>
            <p className="fs-5 mx-auto" style={{ color: 'var(--text)', maxWidth: '800px' }}>
              Our Resources are curated for individuals, families, professionals, and business owners alike — because everyone deserves access to the knowledge that empowers better financial choices.
            </p>
          </motion.div>

          <div className="row g-4">
            {[
              {
                icon: faBook,
                title: "Articles & Guides",
                description: "Step-by-step guides and expert-written articles in simple language",
                features: [
                  "Basics of investing, insurance, and wealth planning",
                  "How to start SIPs or invest in mutual funds",
                  "Smart strategies for tax planning in India",
                  "Financial planning checklists for different age groups"
                ]
              },
              {
                icon: faVideo,
                title: "Videos & Webinars",
                description: "Coming Soon - Visual learning resources",
                features: [
                  "Quick explainers on common financial topics",
                  "Webinar recordings with industry experts",
                  "Founder insights and client success stories",
                  "Financial planning tips in under 3 minutes"
                ],
                comingSoon: true
              },
              {
                icon: faCalculator,
                title: "Financial Tools & Calculators",
                description: "Upcoming free digital tools for smarter choices",
                features: [
                  "SIP Calculator – See investment growth over time",
                  "Retirement Planner – Plan your golden years",
                  "Insurance Needs Estimator – Find right coverage",
                  "Tax Saving Planner – Maximize your savings"
                ],
                comingSoon: true
              },
              {
                icon: faDownload,
                title: "Downloadable PDFs & Checklists",
                description: "Quick-reference guides for busy schedules",
                features: [
                  "Financial Planning Checklist for 2025",
                  "Tax Saving Guide under Section 80C",
                  "10 Questions Before Buying Insurance",
                  "Investment Mistakes to Avoid"
                ],
                comingSoon: true
              }
            ].map((resource, index) => (
              <motion.div
                key={index}
                className="col-md-6 col-lg-3"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={itemVariants}
                transition={{ delay: index * 0.1 }}
              >
                <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: 'var(--white)' }}>
                  <div className="card-body p-4 text-center">
                    <div
                      className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                      style={{
                        width: '80px',
                        height: '80px',
                        backgroundColor: 'var(--light-bg)'
                      }}
                    >
                      <FontAwesomeIcon
                        icon={resource.icon}
                        className="fs-3"
                        style={{ color: 'var(--primary)' }}
                      />
                    </div>
                    <h5 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>
                      {resource.title}
                    </h5>
                    <p style={{ color: 'var(--text)' }} className="mb-3">
                      {resource.description}
                    </p>
                    <ul className="text-start small" style={{ color: 'var(--text)' }}>
                      {resource.features.map((feature, i) => (
                        <li key={i}>{feature}</li>
                      ))}
                    </ul>
                    {resource.comingSoon && (
                      <div className="mt-2">
                        <span
                          className="badge"
                          style={{ backgroundColor: 'var(--secondary)', color: 'var(--heading)' }}
                        >
                          Coming Soon
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="container py-5">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h2
            className="fw-bold text-center mb-5"
            style={{ color: 'var(--heading)' }}
            variants={itemVariants}
          >
            Who This Page is For
          </motion.h2>

          <div className="row g-4">
            {[
              "Young professionals taking their first financial steps",
              "Families balancing multiple financial goals",
              "Retirees looking for peace of mind",
              "Women seeking independence through smart planning",
              "Freelancers & business owners managing irregular income",
              "NRIs navigating India-based finances"
            ].map((audience, index) => (
              <motion.div
                key={index}
                className="col-md-6 col-lg-4"
                variants={itemVariants}
                transition={{ delay: index * 0.1 }}
              >
                <div className="d-flex align-items-center p-3 rounded-3" style={{ backgroundColor: 'var(--light-bg)' }}>
                  <FontAwesomeIcon
                    icon={faCheckCircle}
                    className="me-3"
                    style={{ color: 'var(--primary)' }}
                  />
                  <span style={{ color: 'var(--text)' }}>{audience}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-5 p-4 rounded-3"
            style={{ backgroundColor: 'var(--light-bg)' }}
            variants={itemVariants}
          >
            <p className="fs-5 mb-0" style={{ color: 'var(--text)' }}>
              No matter your background or profession, the SAG Financials Resources section is here to educate, simplify, and support.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Learn With Us */}
      <section className="container-fluid py-5" style={{ backgroundColor: 'var(--primary)', color: 'var(--white)' }}>
        <div className="container">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={containerVariants}
          >
            <motion.h2
              className="fw-bold text-center mb-5"
              variants={itemVariants}
            >
              Why Learn With SAG Financials?
            </motion.h2>

            <div className="row g-4">
              {[
                {
                  title: "22+ Years of Experience",
                  description: "We've helped 100s of clients make informed financial decisions. Our resources reflect real-life questions and concerns."
                },
                {
                  title: "Trusted Certifications",
                  description: "All information backed by AMFI, IRDA, and NISM-certified advisors — not internet myths."
                },
                {
                  title: "We Speak Human, Not Finance",
                  description: "We break down complex topics into relatable advice. Simple explanations for complex concepts."
                },
                {
                  title: "Free. Forever.",
                  description: "Our resources are created to give back — not upsell. No hidden costs, no spam."
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  className="col-md-6"
                  variants={itemVariants}
                  transition={{ delay: index * 0.1 }}
                >
                  <div className="d-flex align-items-start">
                    <FontAwesomeIcon
                      icon={faCheckCircle}
                      className="me-3 mt-1"
                      style={{ color: 'var(--secondary)' }}
                    />
                    <div>
                      <h5 className="fw-bold mb-2">{benefit.title}</h5>
                      <p className="mb-0 opacity-90">{benefit.description}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="container py-5">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={containerVariants}
        >
          <motion.h2
            className="fw-bold text-center mb-5"
            style={{ color: 'var(--heading)' }}
            variants={itemVariants}
          >
            Hear It From Our Clients
          </motion.h2>

          <div className="row g-4">
            {[
              {
                quote: "Before SAG Financials, I thought SIPs and mutual funds were too risky and complicated. After going through their guides and one session, I not only started investing — I finally understood how it works.",
                author: "Richa S., IT Professional"
              },
              {
                quote: "Their tax planning checklist saved me hours during filing season. Clear, simple, and accurate.",
                author: "Vikas D., Small Business Owner"
              }
            ].map((testimonial, index) => (
              <motion.div
                key={index}
                className="col-md-6"
                variants={itemVariants}
                transition={{ delay: index * 0.2 }}
              >
                <div className="card border-0 shadow-sm h-100" style={{ backgroundColor: 'var(--light-bg)' }}>
                  <div className="card-body p-4">
                    <FontAwesomeIcon
                      icon={faComments}
                      className="mb-3"
                      style={{ color: 'var(--primary)' }}
                    />
                    <p className="fs-5 mb-3" style={{ color: 'var(--text)' }}>
                      "{testimonial.quote}"
                    </p>
                    <p className="fw-bold mb-0" style={{ color: 'var(--heading)' }}>
                      — {testimonial.author}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div
            className="text-center mt-5"
            variants={itemVariants}
          >
            <p style={{ color: 'var(--text)' }}>
              Want to share your own learning experience? We'd love to hear from you.
            </p>
          </motion.div>
        </motion.div>
      </section>

      {/* Stay Updated & Contact */}
      <section className="container-fluid py-5" style={{ backgroundColor: 'var(--light-bg)' }}>
        <div className="container">
          <div className="row g-5">
            {/* Newsletter */}
            <motion.div
              className="col-lg-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <h3 className="fw-bold mb-4" style={{ color: 'var(--heading)' }}>Stay Updated</h3>
                <p style={{ color: 'var(--text)' }} className="mb-4">
                  We regularly add new content, guides, and tools — all designed with your needs in mind.
                </p>

                <div className="card border-0 shadow-sm" style={{ backgroundColor: 'var(--white)' }}>
                  <div className="card-body p-4">
                    <h5 className="fw-bold mb-3" style={{ color: 'var(--heading)' }}>
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" style={{ color: 'var(--primary)' }} />
                      Subscribe to our newsletter
                    </h5>
                    <ul style={{ color: 'var(--text)' }}>
                      <li>Monthly financial tips</li>
                      <li>Tax-saving reminders</li>
                      <li>New articles, videos & tool launches</li>
                      <li>Invitations to webinars and Q&As</li>
                    </ul>
                    <button
                      className="btn btn-lg w-100 fw-bold mt-3"
                      style={{
                        backgroundColor: 'var(--primary)',
                        color: 'var(--white)',
                        border: 'none'
                      }}
                    >
                      Join Our Mailing List →
                    </button>
                  </div>
                </div>
              </motion.div>
            </motion.div>

            {/* Contact */}
            <motion.div
              className="col-lg-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              <motion.div variants={itemVariants}>
                <h3 className="fw-bold mb-4" style={{ color: 'var(--heading)' }}>Have a Question?</h3>
                <p style={{ color: 'var(--text)' }} className="mb-4">
                  We're here to help. If you can't find what you're looking for or want us to cover a specific topic, reach out!
                </p>

                <div className="card border-0 shadow-sm" style={{ backgroundColor: 'var(--white)' }}>
                  <div className="card-body p-4">
                    {[
                      { icon: faEnvelope, text: "Email:", detail: "contact@sagfinancials.com" },
                      { icon: faPhone, text: "WhatsApp or Call:", detail: "+91 7011468336" },
                      { icon: faGlobe, text: "Visit:", detail: "www.sagfinancials.com" }
                    ].map((contact, index) => (
                      <div key={index} className="d-flex align-items-center mb-3">
                        <FontAwesomeIcon
                          icon={contact.icon}
                          className="me-3"
                          style={{ color: 'var(--primary)', width: '20px' }}
                        />
                        <div>
                          <strong style={{ color: 'var(--heading)' }}>{contact.text}</strong>
                          <span className="ms-2" style={{ color: 'var(--text)' }}>{contact.detail}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ResourcesPage;